import { Button } from "@/components/ui/button";
import { CalendarDialog } from "@/components/CalendarDialog";
import { ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const PROOF_ITEMS = [
  "Built for Australian trade businesses",
  "Websites, Google visibility and reviews",
  "Clear reporting without the agency jargon",
];

export function HeroSection() {
  return (
    <section
      id="hero-section"
      className="relative bg-background text-foreground overflow-hidden"
    >
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Soft brand blob */}
      <div className="absolute -right-24 top-10 z-0 w-[380px] h-[380px] rounded-full bg-primary/[0.07] blur-3xl pointer-events-none" />

      {/* Dotted accent grid, top right */}
      <div
        className="absolute right-4 top-16 z-0 w-40 h-40 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--primary) / 0.5) 1.5px, transparent 1.5px)",
          backgroundSize: "16px 16px",
          maskImage: "linear-gradient(200deg, black, transparent 75%)",
          WebkitMaskImage: "linear-gradient(200deg, black, transparent 75%)",
        }}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-5 sm:px-6 pt-28 sm:pt-36 lg:pt-40 pb-16 sm:pb-20 lg:pb-24 text-center flex flex-col items-center">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 sm:mb-10"
        >
          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-[9px] sm:text-xs font-semibold tracking-[0.08em] uppercase text-primary">
            Built for Australian Trade &amp; Service Businesses
          </span>
        </motion.div>

        {/* Headline — rendered immediately for fast LCP, no entrance animation */}
        <h1 className="text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-[-0.03em] text-foreground leading-[1.05] mb-6 sm:mb-7 max-w-4xl text-balance">
          Digital Marketing That
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          Gets You <span className="heading-accent">More Jobs.</span>
        </h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-[34ch] sm:max-w-2xl leading-[1.7] mb-8"
        >
          We help Australian trade and service businesses get found, build trust
          and turn more local searches into calls and quote requests.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5 mb-12 sm:mb-14 w-full sm:w-auto"
        >
          <CalendarDialog
            buttonVariant="default"
            buttonSize="lg"
            buttonClassName="w-full sm:w-auto min-w-0 sm:min-w-[260px] h-[56px] text-[15px] sm:text-base font-bold rounded-2xl shadow-lg shadow-primary/20"
          />
          <Button
            variant="outline"
            size="lg"
            asChild
            className="group w-full sm:w-auto min-w-0 sm:min-w-[200px] h-[56px] text-[15px] sm:text-base font-semibold rounded-2xl"
          >
            <Link to="/services">
              See Our Services
              <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </motion.div>

        {/* Proof strip */}
        <motion.div
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border/60 w-full max-w-4xl bg-background/50 rounded-2xl border border-border/40 px-6 py-6 sm:py-7"
        >
          {PROOF_ITEMS.map((item) => (
            <div
              key={item}
              className="flex sm:flex-col items-center justify-start sm:justify-center gap-3 sm:gap-2.5 px-2 sm:px-4 py-3 sm:py-0 text-left sm:text-center"
            >
              <Check className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={2.5} />
              <span className="text-[0.8125rem] sm:text-sm font-medium text-foreground/80 leading-snug text-balance">
                {item}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
